import { Head, Link } from '@inertiajs/react';

/**
 * Terms of Service page.
 * Simple, readable terms covering UGC rights and community guidelines.
 */
export default function Tos() {
    return (
        <>
            <Head title="Terms of Service" />

            <div className="min-h-screen bg-slate-900 py-12 px-4">
                <div className="max-w-3xl mx-auto">
                    {/* Header */}
                    <div className="mb-8">
                        <Link
                            href="/"
                            className="text-purple-400 hover:text-purple-300 transition-colors text-sm"
                        >
                            &larr; Back to Home
                        </Link>
                        <h1 className="text-3xl font-bold text-white mt-4">Terms of Service</h1>
                        <p className="text-gray-400 mt-2">Last updated: January 2026</p>
                    </div>

                    {/* Content */}
                    <div className="bg-slate-800 rounded-lg border border-slate-700 p-8 space-y-8">
                        {/* Welcome */}
                        <section>
                            <h2 className="text-xl font-semibold text-white mb-3">Welcome to Sellune</h2>
                            <p className="text-gray-300 leading-relaxed">
                                Sellune is a collaborative worldbuilding platform where users create and share
                                content together. By using Sellune, you agree to these terms.
                            </p>
                        </section>

                        {/* Community Guidelines */}
                        <section>
                            <h2 className="text-xl font-semibold text-white mb-3">Community Guidelines</h2>
                            <p className="text-gray-300 leading-relaxed mb-4">
                                Sellune is part of the Moth House community. All users must follow the
                                rules and guidelines of the{' '}
                                    Moth House Discord server
                            </p>
                            <p className="text-gray-300 leading-relaxed">
                                Be respectful, creative, and collaborative. Content that violates community
                                guidelines may be removed, and accounts may be suspended.
                            </p>
                        </section>

                        {/* User-Generated Content */}
                        <section>
                            <h2 className="text-xl font-semibold text-white mb-3">Your Content</h2>
                            <p className="text-gray-300 leading-relaxed mb-4">
                                You retain ownership of the content you create on Sellune, including text,
                                images, and other creative works.
                            </p>
                            <p className="text-gray-300 leading-relaxed mb-4">
                                By posting content on Sellune, you grant us a license to host, display, and
                                share your content with other users as part of the platform. This allows
                                other players to read your hex descriptions, view your maps, and interact
                                with your creations.
                            </p>
                            <p className="text-gray-300 leading-relaxed">
                                You are responsible for ensuring you have the right to post any content you
                                upload. Do not post content that infringes on others' rights.
                            </p>
                            <p className="text-gray-300 leading-relaxed">
                                AI-generated content is not allowed.
                            </p>
                        </section>

                        {/* What We Do */}
                        <section>
                            <h2 className="text-xl font-semibold text-white mb-3">How We Use Your Content</h2>
                            <ul className="text-gray-300 space-y-2 list-disc list-inside">
                                <li>Display your content to other users on the platform</li>
                                <li>Store your content on our servers</li>
                                <li>Include your content in the shared world that all players explore</li>
                                <li>Moderate content to ensure community guidelines are followed</li>
                            </ul>
                        </section>

                        {/* Account */}
                        <section>
                            <h2 className="text-xl font-semibold text-white mb-3">Your Account</h2>
                            <p className="text-gray-300 leading-relaxed">
                                You sign in using Discord. You are responsible for maintaining access to your
                                Discord account. We may suspend or terminate accounts that violate these terms
                                or community guidelines.
                            </p>
                        </section>

                        {/* Changes */}
                        <section>
                            <h2 className="text-xl font-semibold text-white mb-3">Changes to Terms</h2>
                            <p className="text-gray-300 leading-relaxed">
                                We may update these terms from time to time. Continued use of Sellune after
                                changes means you accept the new terms.
                            </p>
                        </section>

                        {/* Contact */}
                        <section>
                            <h2 className="text-xl font-semibold text-white mb-3">Questions?</h2>
                            <p className="text-gray-300 leading-relaxed">
                                If you have questions about these terms, reach out to us on the{' '}
                                    Moth House Discord server.
                            </p>
                        </section>
                    </div>

                    {/* Footer */}
                    <div className="mt-8 text-center text-gray-500 text-sm">
                        <p>Sellune - A collaborative worldbuilding experience</p>
                    </div>
                </div>
            </div>
        </>
    );
}
